import type { RouteRecordRaw } from 'vue-router'
import { AppModule } from '@/config/types'
import ChatsAsyncComponentWrapper from '@/components/views/chats/ChatsAsyncComponentWrapper.vue'
import { getMessengerSettingsRoutes } from '@/routes/user/messenger/messengerSettings'
import { START_CHAT_QUERY_PARAM_NAME } from '@/data/models/domain/chats/types'
import type { ComponentProps } from '@/composition/types'
import type { TmRoutesDictionary } from '@/services/route/routerTypes'
import { MessengerRouteNames } from '@/routes/user/messenger/types'
import type { NavigationOrderResolverParams } from '@/services/resolvers/types'

export const getChatsRoutes = (): RouteRecordRaw[] => [
  {
    name: MessengerRouteNames.root,
    path: 'messenger',
    redirect({ params, query }) {
      return {
        name: MessengerRouteNames.index,
        params: params.phone ? { phone: params.phone } : {},
        query: {
          id: query.id,
          dt1: query.dt1,
          dt2: query.dt2,
          [START_CHAT_QUERY_PARAM_NAME]: query[START_CHAT_QUERY_PARAM_NAME],
        },
      }
    },
    meta: {
      tags: ['USER_TOP_MENU'],
      modules: [
        AppModule.SenderSettingsCountries,
        AppModule.Chat,
        AppModule.ContactsUnsubscribed,
        AppModule.ContactsBlocked,
        AppModule.Contacts,
        AppModule.ContactList,
        AppModule.Compose,
        AppModule.MessageSend,
        AppModule.RecentRecipient,
        AppModule.SentSmsBase,
        AppModule.Tasks,
        AppModule.HistoryReceivedSms,
        AppModule.Tags,
        AppModule.Instagram,
        AppModule.Facebook,
        AppModule.WhatsApp,
        AppModule.WebWidgets,
        AppModule.NavigationOrder,
      ],
      resolvers: [
        {
          service: 'ChatInitSettingsResolverService',
        },
        {
          service: 'NavigationOrderResolver',
          params: { serviceId: 'ChatNavigationOrderService' } satisfies NavigationOrderResolverParams,
        },
      ],
    },
    children: [
      {
        name: MessengerRouteNames.index,
        path: 'chat/:phone?',
        props: (route) => {
          const tagQueryKey: keyof NonNullable<TmRoutesDictionary[MessengerRouteNames.index]['query']> = 'tags'
          const contactTagQueryKey: keyof NonNullable<TmRoutesDictionary[MessengerRouteNames.index]['query']> =
            'contactTags'
          return {
            id: route.query.id as string,
            dtStart: route.query.dt1,
            dtEnd: route.query.dt2,
            filter: route.query.filter,
            phone: route.params.phone,
            tags: route.query[tagQueryKey],
            contactTags: route.query[contactTagQueryKey],
          } satisfies ComponentProps<typeof ChatsAsyncComponentWrapper>
        },
        meta: {
          tabTitle: 'pageTabTitle.user.chats.index',
          modules: [
            AppModule.SenderSettingsCountries,
            AppModule.Chat,
            AppModule.Typing,
            AppModule.ContactsUnsubscribed,
            AppModule.ContactsBlocked,
            AppModule.Contacts,
            AppModule.ContactList,
            AppModule.FilteredViews,
            AppModule.Segments,
            AppModule.Compose,
            AppModule.MessageSend,
            AppModule.RecentRecipient,
            AppModule.SentSmsBase,
            AppModule.Tasks,
            AppModule.AlsoHere,
            AppModule.WebWidgets,
            AppModule.PointAi,
            AppModule.Note,
          ],
          nonBlockingResolvers: [
            {
              service: 'FetchAllAccountsResolver',
            },
            {
              service: 'TypingResolver',
            },
            {
              service: 'ChatResolver',
            },
            {
              service: 'PointAiChatResolver',
            },
            {
              service: 'AlsoHereResolver',
            },
            {
              service: 'SegmentsForContactChatResolverService',
            },
            {
              service: 'TagsFromRouteQueryResolverService',
            },
          ],
        },
        component: ChatsAsyncComponentWrapper,
      },
      ...getMessengerSettingsRoutes(),
    ],
  },
]
